import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import Select from "react-select";
import ReactQuill from "react-quill";
import "quill/dist/quill.snow.css";
import "react-quill/dist/quill.snow.css";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";
import { RentCorpSelectionTable } from "./RentCorpSelectionTable";
import {AosReceiptSelectionTable} from "./AosReceiptSelectionTable";
import {AosManagerSelectionTable} from "./AosManagerSelectionTable";

export const InputPopup = ({
    layer = true,
    onClose,
    title,
    description,
    width = "480px",
    inputs = [],
    selects = [],
    radios = [],
    checkboxes = [],
    textareas = [],
    quillEditors = [],
    modules = [], // 모듈 (ex: { type: "rentCorpSelection", props: {...} })
    buttons = [],
}) => {
    const [inputValues, setInputValues] = useState({});
    const [selectValues, setSelectValues] = useState({});
    const [radioValues, setRadioValues] = useState({});
    const [checkboxValues, setCheckboxValues] = useState({});
    const [textareaValues, setTextareaValues] = useState({});
    const [quillValues, setQuillValues] = useState({});

    // 모듈별 상태 관리
    const [moduleStates, setModuleStates] = useState({});

    useEffect(() => {
        document.body.classList.add("scroll-lock");

        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    // props 변경 시 상태 업데이트
    useEffect(() => {
        // 초기값 설정
        const initialInputValues = {};
        inputs.forEach((input) => {
            initialInputValues[input.name] = input.value || "";
        });
        setInputValues(initialInputValues);

        const initialSelectValues = {};
        selects.forEach((select) => {
            initialSelectValues[select.name] = select.value || null;
        });
        setSelectValues(initialSelectValues);

        const initialRadioValues = {};
        radios.forEach((radio) => {
            initialRadioValues[radio.name] = radio.value || "";
        });
        setRadioValues(initialRadioValues);

        const initialCheckboxValues = {};
        checkboxes.forEach((checkbox) => {
            initialCheckboxValues[checkbox.name] = checkbox.value || [];
        });
        setCheckboxValues(initialCheckboxValues);

        const initialTextareaValues = {};
        textareas.forEach((textarea) => {
            initialTextareaValues[textarea.name] = textarea.value || "";
        });
        setTextareaValues(initialTextareaValues);

        const initialQuillValues = {};
        quillEditors.forEach((quill) => {
            initialQuillValues[quill.name] = quill.value || "";
        });
        setQuillValues(initialQuillValues);

        // 모듈 초기 상태 설정
        const initialModuleStates = {};
        modules.forEach((module) => {
            if (module.type === "rentCorpSelection") {
                initialModuleStates.selectedRentCorp = null;
            } else if (module.type === "datePicker") {
                initialModuleStates[module.props.name] = module.props.value || new Date();
            }
        });
        setModuleStates(initialModuleStates);
    }, [
        JSON.stringify(inputs),
        JSON.stringify(selects),
        JSON.stringify(radios),
        JSON.stringify(checkboxes),
        JSON.stringify(textareas),
        JSON.stringify(quillEditors),
        JSON.stringify(modules),
    ]);

    const handleInputChange = (name, value) => {
        setInputValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSelectChange = (name, value) => {
        setSelectValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleRadioChange = (name, value) => {
        setRadioValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCheckboxChange = (name, value, checked) => {
        setCheckboxValues((prev) => {
            const currentValues = prev[name] || [];
            if (checked) {
                return {
                    ...prev,
                    [name]: [...currentValues, value],
                };
            } else {
                return {
                    ...prev,
                    [name]: currentValues.filter((item) => item !== value),
                };
            }
        });
    };

    const handleTextareaChange = (name, value) => {
        setTextareaValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleQuillChange = (name, value) => {
        setQuillValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleModuleStateChange = (key, value) => {
        setModuleStates((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleButtonClick = (button) => {
        if (button.onClick) {
            const result = button.onClick({
                inputValues,
                selectValues,
                radioValues,
                checkboxValues,
                textareaValues,
                quillValues,
                moduleStates,
            });

            // onClick이 false를 반환하면 모달을 닫지 않음
            if (result === false) {
                return;
            }
        }
        if (button.closeOnClick !== false) {
            onClose();
        }
    };

    const renderModule = (module) => {
        switch (module.type) {
            case "rentCorpSelection":
                return (
                    <RentCorpSelectionTable
                        key="rentCorpSelection"
                        selectedRentCorp={moduleStates.selectedRentCorp}
                        onSelect={(rentCorp) => handleModuleStateChange("selectedRentCorp", rentCorp)}
                        {...(module.props || {})}
                    />
                );
            case "aosReceiptSelection":
                return (
                    <AosReceiptSelectionTable
                        key="aosReceiptSelection"
                        aosReceiptData={module.selectItems}
                        selectedAosReceipt={moduleStates.selectedAosReceipt}
                        onSelect={(aosReceipt) => {
                            handleModuleStateChange("selectedAosReceipt", aosReceipt)
                            console.log(module.selectItems.data[aosReceipt]);
                            module.setSelectedAosItem(module.selectItems.data[aosReceipt]);
                        }}
                        {...(module.props || {})}
                    />
                );
            case "aosManagerSelection":
                return (
                    <AosManagerSelectionTable
                        key="aosManagerSelection"
                        aosManagerData={module.selectItems}
                        selectedAosManager={moduleStates.selectedAosManager}
                        onSelect={(aosManager) => {
                            handleModuleStateChange("selectedAosManager", aosManager)
                            console.log(module.selectItems.data[aosManager])
                            module.setSelectedAosManager(module.selectItems.data[aosManager]);
                        }}
                        {...(module.props || {})}
                    />
                );
            case "datePicker":
                return (
                    <div key="datePicker" className="input-group">
                        {module.props.label && <label>{module.props.label}</label>}
                        <DatePicker
                            locale={ko}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={20}
                            dateFormat="yyyy.MM.dd"
                            onKeyDown={(e) => e.preventDefault()}
                            selected={moduleStates[module.props.name] || module.props.value}
                            onChange={(date) => handleModuleStateChange(module.props.name, date)}
                            placeholder={module.props.placeholder}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>

            <div className={`popup input-popup`} style={{ width }}>
                <div className="popup-content">
                    <div className="content-title">
                        <span>{title}</span>
                    </div>
                    <div className="content-description">
                        <p>{description}</p>
                    </div>
                    <div className="content-inputs">
                        {radios.map((radio, index) => (
                            <div key={`radio-${index}`} className="radio-group">
                                {radio.label && <label>{radio.label}</label>}
                                <div className="radio-options">
                                    {radio.options.map((option, optionIndex) => (
                                        <div key={optionIndex} className="radiobox">
                                            <input
                                                type="radio"
                                                name={radio.name}
                                                id={`${radio.name}_${option.value}`}
                                                checked={radioValues[radio.name] === option.value}
                                                onChange={() => handleRadioChange(radio.name, option.value)}
                                                disabled={radio.disabled}
                                            />
                                            <label htmlFor={`${radio.name}_${option.value}`}>
                                                <span>{option.label}</span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {inputs.map((input, index) => (
                            <div key={index} className="input-group">
                                {input.label && <label>{input.label}</label>}
                                <input
                                    type={input.type || "text"}
                                    name={input.name}
                                    value={inputValues[input.name] || ""}
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        // 숫자 포맷팅이 필요한 필드인 경우 콤마 포맷팅 적용
                                        if (input.format === "number" || input.name === "depositAmount" || input.name === "depositPrice") {
                                            const numericValue = value.replace(/[^0-9]/g, "");
                                            value = numericValue ? numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";
                                        }
                                        handleInputChange(input.name, value);
                                    }}
                                    placeholder={input.placeholder}
                                    disabled={input.disabled}
                                />
                            </div>
                        ))}

                        {selects.map((select, index) => (
                            <div key={`select-${index}`} className="select-group">
                                {select.label && <label>{select.label}</label>}
                                <Select
                                    className="filter-select"
                                    classNamePrefix="filter-select"
                                    value={selectValues[select.name]}
                                    onChange={(value) => handleSelectChange(select.name, value)}
                                    options={select.options}
                                    placeholder={select.placeholder}
                                    isDisabled={select.disabled}
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                />
                            </div>
                        ))}

                        {checkboxes.map((checkbox, index) => (
                            <div key={`checkbox-${index}`} className="checkbox-group">
                                {checkbox.label && <label>{checkbox.label}</label>}
                                <div className="checkbox-options">
                                    {checkbox.options.map((option, optionIndex) => (
                                        <div key={optionIndex} className="checkbox">
                                            <input
                                                type="checkbox"
                                                id={`${checkbox.name}_${option.value}`}
                                                checked={(checkboxValues[checkbox.name] || []).includes(option.value)}
                                                onChange={(e) => handleCheckboxChange(checkbox.name, option.value, e.target.checked)}
                                                disabled={checkbox.disabled}
                                            />
                                            <label htmlFor={`${checkbox.name}_${option.value}`}>
                                                <span>{option.label}</span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {textareas.map((textarea, index) => (
                            <div key={`textarea-${index}`} className="textarea-group">
                                {textarea.label && <label>{textarea.label}</label>}
                                <textarea
                                    name={textarea.name}
                                    value={textareaValues[textarea.name] || ""}
                                    onChange={(e) => handleTextareaChange(textarea.name, e.target.value)}
                                    placeholder={textarea.placeholder}
                                    disabled={textarea.disabled}
                                    rows={textarea.rows || 4}
                                    style={{ width: "100%", resize: "vertical" }}
                                />
                            </div>
                        ))}

                        {quillEditors.map((quill, index) => (
                            <div key={`quill-${index}`} className="quill-group">
                                {quill.label && <label>{quill.label}</label>}
                                <ReactQuill
                                    theme="snow"
                                    style={{ width: "100%", height: quill.height || "" }}
                                    value={quillValues[quill.name] || ""}
                                    onChange={(value) => handleQuillChange(quill.name, value)}
                                    placeholder={quill.placeholder}
                                    readOnly={quill.disabled}
                                />
                            </div>
                        ))}

                        {/* 모듈 렌더링 */}
                        {modules.map((module, index) => {
                            if(!module.showYn || module.showYn === "Y") {
                                return <div key={`module-${index}`}>{renderModule(module)}</div>
                            } else {
                                return <></>
                            }
                        })}
                    </div>
                </div>
                <div className="popup-bottom">
                    {buttons.map((button, index) => (
                        <button key={index} className={`btn ${button.className}`} disabled={button.disabled} onClick={() => handleButtonClick(button)}>
                            {button.label}
                        </button>
                    ))}
                </div>
            </div>
        </>,
        document.body
    );
};
